import React from 'react';
import { Box, Card, useMediaQuery } from '@mui/material';
import { useTheme } from '@emotion/react';
import Shimmer from 'ui-components/Shimmer';

// Placeholder shaped like PersonalDetails.js, so nothing shifts when data lands.
// Every number below is taken from that file.

const CARD_RADIUS = '16px';
const BORDER = '#EAECF0';

const cardStyles = {
  padding: { xs: '1.25rem', md: '1.5rem' },
  boxShadow: '0px 1px 3px rgba(16,24,40,0.06)',
  backgroundColor: '#ffffff',
  border: `1px solid ${BORDER}`,
  borderRadius: CARD_RADIUS,
  overflow: 'visible',
};

// CardHeader is a 1.125rem/700 title on a 1.2 line box, with mb 1.25rem.
const CardTitle = ({ width }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '1.25rem' }}>
    <Shimmer width={width} height={22} radius={4} />
    <Shimmer width={24} height={24} radius={6} />
  </Box>
);

const ProfileSkeleton = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        maxWidth: '1040px',
        marginInline: 'auto',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.25rem',
        paddingBottom: '2rem',
      }}
    >
      {/* Header: 8rem avatar, name / type / location, contact pills */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'center', sm: 'flex-start' },
          gap: { xs: '1.25rem', sm: '1.75rem' },
          padding: { xs: '0.5rem 0.5rem 0.25rem', sm: '0.75rem 0.5rem 0.25rem' },
        }}
      >
        <Shimmer
          width={isSmallScreen ? '7.5rem' : '8rem'}
          height={isSmallScreen ? '7.5rem' : '8rem'}
          radius={16}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.4rem',
            flex: 1,
            minWidth: 0,
            width: '100%',
            alignItems: { xs: 'center', sm: 'flex-start' },
            paddingTop: { sm: '0.125rem' },
          }}
        >
          <Shimmer width={isSmallScreen ? '60%' : '17rem'} height={30} radius={6} />
          <Shimmer width="4.5rem" height={16} radius={4} />
          <Shimmer width="13rem" height={16} radius={4} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.625rem', marginTop: '0.375rem' }}>
            <Shimmer width="12rem" height={37} radius={8} />
            <Shimmer width="6.5rem" height={37} radius={8} />
            <Shimmer width="7.5rem" height={37} radius={8} />
          </Box>
        </Box>
      </Box>

      {/* Overview */}
      <Card sx={cardStyles}>
        <CardTitle width="8rem" />
        <Shimmer width="100%" height={12} radius={4} sx={{ mb: '0.55rem' }} />
        <Shimmer width="72%" height={12} radius={4} />
      </Card>

      {/* Investment Focus: 5 tiles, 5 / 3 / 1 per row */}
      <Card sx={cardStyles}>
        <CardTitle width="11rem" />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
            gap: { xs: '0.75rem', sm: '1rem' },
          }}
        >
          {Array.from({ length: 5 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: '12px',
                padding: { xs: '0.875rem', sm: '1.5rem 0.875rem 1rem' },
                display: 'flex',
                flexDirection: { xs: 'row', sm: 'column' },
                alignItems: 'center',
                gap: { xs: '0.875rem', sm: '0.75rem' },
                minWidth: 0,
              }}
            >
              <Shimmer
                width={isSmallScreen ? 44 : 56}
                height={isSmallScreen ? 44 : 56}
                radius={12}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.6rem',
                  minWidth: 0,
                  flex: 1,
                  width: '100%',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                }}
              >
                <Shimmer width="70%" height={14} radius={4} />
                <Shimmer width="90%" height={11} radius={4} />
              </Box>
              {!isSmallScreen && <Shimmer width="70%" height={3} radius={1} sx={{ marginTop: '0.5rem' }} />}
            </Box>
          ))}
        </Box>
      </Card>

      {/* Other Information: three icon + label / value rows */}
      <Card sx={cardStyles}>
        <CardTitle width="10rem" />
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
              paddingBlock: '0.875rem',
              borderTop: i === 0 ? 'none' : `1px solid ${BORDER}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
              <Shimmer width={34} height={34} radius={8} />
              <Shimmer width={i === 2 ? '11rem' : '8rem'} height={13} radius={4} />
            </Box>
            <Shimmer width={i === 2 ? '2rem' : '9rem'} height={13} radius={4} />
          </Box>
        ))}
      </Card>

      {/* Privacy note */}
      <Shimmer width="21rem" height={33} radius={10} sx={{ alignSelf: 'center', marginBlock: '0.75rem' }} />
    </Box>
  );
};

export default ProfileSkeleton;
